class CustomVideo extends HTMLElement {
  constructor() {
    super();

    this.videoElem = this.querySelector("video");
    this.playButton = this.querySelector(".play-button");
    this.progressWrap = this.querySelector(".video-progress");
    this.progressBar = this.querySelector(".video-progress__bar");

    this.hasProgressControl = this.hasAttribute("data-progress");

    this.isDraggingProgress = false;
    this.wasPlayingBeforeDrag = false;

    this.isSeeking = false;
    this.pendingSeekTime = null;

    this.slideObserver = null;

    this.handlePlayButton = this.handlePlayButton.bind(this);
    this.handleVideoClick = this.handleVideoClick.bind(this);
    this.handleVideoPlay = this.handleVideoPlay.bind(this);
    this.updateProgress = this.updateProgress.bind(this);
    this.handleVideoEnded = this.handleVideoEnded.bind(this);
    this.handleVideoSeeked = this.handleVideoSeeked.bind(this);

    this.handleProgressPointerDown =
      this.handleProgressPointerDown.bind(this);

    this.handleProgressPointerMove =
      this.handleProgressPointerMove.bind(this);

    this.handleProgressPointerUp =
      this.handleProgressPointerUp.bind(this);
  }

  connectedCallback() {
    if (!this.videoElem) return;

    /*
     * Контроллеры изначально скрыты у всех видео.
     */
    this.videoElem.controls = false;

    this.videoElem.addEventListener(
      "click",
      this.handleVideoClick
    );

    this.videoElem.addEventListener(
      "play",
      this.handleVideoPlay
    );

    this.videoElem.addEventListener(
      "timeupdate",
      this.updateProgress
    );

    this.videoElem.addEventListener(
      "ended",
      this.handleVideoEnded
    );

    this.videoElem.addEventListener(
      "loadedmetadata",
      this.updateProgress
    );

    this.videoElem.addEventListener(
      "seeked",
      this.handleVideoSeeked
    );

    if (this.playButton) {
      this.playButton.addEventListener(
        "click",
        this.handlePlayButton
      );
    }

    if (
      this.hasProgressControl &&
      this.progressWrap
    ) {
      this.progressWrap.addEventListener(
        "pointerdown",
        this.handleProgressPointerDown
      );
    }

    this.observeSlideState();
  }

  observeSlideState() {
    const slide = this.closest(".swiper-slide");

    if (!slide) return;

    this.slideObserver = new MutationObserver(() => {
      const isActive = slide.classList.contains(
        "swiper-slide-active"
      );

      if (!isActive && !this.videoElem.paused) {
        this.pauseVideo();
      }
    });

    this.slideObserver.observe(slide, {
      attributes: true,
      attributeFilter: ["class"]
    });
  }

  pauseOtherVideos() {
    document
      .querySelectorAll("custom-video")
      .forEach((customVideo) => {
        if (
          customVideo !== this &&
          typeof customVideo.pauseVideo === "function"
        ) {
          customVideo.pauseVideo();
        }
      });
  }

  handleVideoPlay() {
    this.pauseOtherVideos();

    /*
     * Контроллеры включаем только для первого типа слайдера,
     * где на custom-video есть data-progress.
     */
    if (this.hasProgressControl) {
      this.videoElem.controls = true;
    }

    this.videoElem.classList.add("video-play");

    this.classList.add(
      "is-playing",
      "video-started"
    );
  }

  async playVideo() {
    if (!this.videoElem) return;

    this.pauseOtherVideos();

    try {
      await this.videoElem.play();

      /*
       * Для слайдера без data-progress
       * controls всегда останутся false.
       */
      this.videoElem.controls =
        this.hasProgressControl;

      this.videoElem.classList.add("video-play");

      this.classList.add(
        "is-playing",
        "video-started"
      );
    } catch (error) {
      this.videoElem.controls = false;

      console.error(
        "Video playback error:",
        error
      );
    }
  }

  pauseVideo() {
    if (!this.videoElem) return;

    this.videoElem.pause();

    /*
     * После паузы снова скрываем контроллеры.
     */
    this.videoElem.controls = false;

    this.videoElem.classList.remove("video-play");
    this.classList.remove("is-playing");
  }

  toggleVideo() {
    if (!this.videoElem) return;

    if (this.videoElem.paused) {
      this.playVideo();
    } else {
      this.pauseVideo();
    }
  }

  updateProgress() {
    if (
      !this.hasProgressControl ||
      !this.videoElem ||
      !this.progressWrap ||
      !this.progressBar ||
      !Number.isFinite(this.videoElem.duration) ||
      this.videoElem.duration <= 0
    ) {
      return;
    }

    if (this.isDraggingProgress) return;

    const progress =
      (
        this.videoElem.currentTime /
        this.videoElem.duration
      ) * 100;

    this.progressBar.style.width = `${progress}%`;

    this.progressWrap.style.setProperty(
      "--progress",
      `${progress}%`
    );
  }

  setVideoProgress(clientX) {
    if (
      !this.hasProgressControl ||
      !this.videoElem ||
      !this.progressWrap ||
      !this.progressBar ||
      !Number.isFinite(this.videoElem.duration) ||
      this.videoElem.duration <= 0
    ) {
      return;
    }

    const rect =
      this.progressWrap.getBoundingClientRect();

    if (!rect.width) return;

    const position = Math.min(
      Math.max(clientX - rect.left, 0),
      rect.width
    );

    const progress = position / rect.width;
    const progressPercent = progress * 100;
    const seekTime =
      progress * this.videoElem.duration;

    this.progressBar.style.width =
      `${progressPercent}%`;

    this.progressWrap.style.setProperty(
      "--progress",
      `${progressPercent}%`
    );

    this.pendingSeekTime = seekTime;

    if (!this.isSeeking) {
      this.seekToPendingTime();
    }
  }

  seekToPendingTime() {
    if (
      !this.videoElem ||
      this.pendingSeekTime === null
    ) {
      return;
    }

    const seekTime = this.pendingSeekTime;

    this.pendingSeekTime = null;
    this.isSeeking = true;

    this.videoElem.currentTime = seekTime;
  }

  handleVideoSeeked() {
    this.isSeeking = false;

    if (this.pendingSeekTime !== null) {
      this.seekToPendingTime();
      return;
    }

    if (
      !this.isDraggingProgress &&
      this.wasPlayingBeforeDrag
    ) {
      this.wasPlayingBeforeDrag = false;
      this.playVideo();
    }
  }

  handleProgressPointerDown(event) {
    event.preventDefault();
    event.stopPropagation();

    if (
      !this.hasProgressControl ||
      !this.videoElem ||
      !this.progressWrap
    ) {
      return;
    }

    this.isDraggingProgress = true;

    this.wasPlayingBeforeDrag =
      !this.videoElem.paused;

    if (this.wasPlayingBeforeDrag) {
      /*
       * Здесь вызываем pause() напрямую,
       * чтобы controls не скрывались во время перемотки.
       */
      this.videoElem.pause();
    }

    this.progressWrap.setPointerCapture(
      event.pointerId
    );

    this.setVideoProgress(event.clientX);

    this.progressWrap.addEventListener(
      "pointermove",
      this.handleProgressPointerMove
    );

    this.progressWrap.addEventListener(
      "pointerup",
      this.handleProgressPointerUp
    );

    this.progressWrap.addEventListener(
      "pointercancel",
      this.handleProgressPointerUp
    );
  }

  handleProgressPointerMove(event) {
    if (!this.isDraggingProgress) return;

    event.preventDefault();
    event.stopPropagation();

    this.setVideoProgress(event.clientX);
  }

  handleProgressPointerUp(event) {
    if (!this.isDraggingProgress) return;

    event.preventDefault();
    event.stopPropagation();

    this.isDraggingProgress = false;

    if (
      this.progressWrap &&
      this.progressWrap.hasPointerCapture(
        event.pointerId
      )
    ) {
      this.progressWrap.releasePointerCapture(
        event.pointerId
      );
    }

    if (this.progressWrap) {
      this.progressWrap.removeEventListener(
        "pointermove",
        this.handleProgressPointerMove
      );

      this.progressWrap.removeEventListener(
        "pointerup",
        this.handleProgressPointerUp
      );

      this.progressWrap.removeEventListener(
        "pointercancel",
        this.handleProgressPointerUp
      );
    }

    if (
      this.pendingSeekTime !== null &&
      !this.isSeeking
    ) {
      this.seekToPendingTime();
      return;
    }

    if (
      !this.isSeeking &&
      this.wasPlayingBeforeDrag
    ) {
      this.wasPlayingBeforeDrag = false;
      this.playVideo();
    }
  }

  handleVideoEnded() {
    if (!this.videoElem) return;

    this.videoElem.controls = false;
    this.videoElem.classList.remove("video-play");

    this.classList.remove(
      "is-playing",
      "video-started"
    );

    this.videoElem.currentTime = 0;

    this.isSeeking = false;
    this.pendingSeekTime = null;
    this.wasPlayingBeforeDrag = false;

    if (this.progressBar) {
      this.progressBar.style.width = "0%";
    }

    if (this.progressWrap) {
      this.progressWrap.style.setProperty(
        "--progress",
        "0%"
      );
    }
  }

  handlePlayButton(event) {
    event.preventDefault();
    event.stopPropagation();

    this.toggleVideo();
  }

  handleVideoClick(event) {
    /*
     * Когда нативные controls уже видны,
     * не вмешиваемся в клики по самому video.
     */
    if (
      this.hasProgressControl &&
      this.videoElem.controls
    ) {
      return;
    }

    event.preventDefault();
    this.toggleVideo();
  }

  disconnectedCallback() {
    if (this.slideObserver) {
      this.slideObserver.disconnect();
      this.slideObserver = null;
    }

    if (this.playButton) {
      this.playButton.removeEventListener(
        "click",
        this.handlePlayButton
      );
    }

    if (this.videoElem) {
      this.videoElem.removeEventListener(
        "click",
        this.handleVideoClick
      );

      this.videoElem.removeEventListener(
        "play",
        this.handleVideoPlay
      );

      this.videoElem.removeEventListener(
        "timeupdate",
        this.updateProgress
      );

      this.videoElem.removeEventListener(
        "ended",
        this.handleVideoEnded
      );

      this.videoElem.removeEventListener(
        "loadedmetadata",
        this.updateProgress
      );

      this.videoElem.removeEventListener(
        "seeked",
        this.handleVideoSeeked
      );
    }

    if (this.progressWrap) {
      this.progressWrap.removeEventListener(
        "pointerdown",
        this.handleProgressPointerDown
      );

      this.progressWrap.removeEventListener(
        "pointermove",
        this.handleProgressPointerMove
      );

      this.progressWrap.removeEventListener(
        "pointerup",
        this.handleProgressPointerUp
      );

      this.progressWrap.removeEventListener(
        "pointercancel",
        this.handleProgressPointerUp
      );
    }
  }
}

if (!customElements.get("custom-video")) {
  customElements.define(
    "custom-video",
    CustomVideo
  );
}